import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/i18n/LanguageContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Package, Download, Loader2, Calendar, Printer, MapPin, Trash2, Pencil, User, Clock } from 'lucide-react';

import { API } from '@/lib/apiBase';

function OrderStatusBadge({ status, t }) {
  if (status === 'paid') return <Badge className="bg-green-500/10 text-green-500 border-green-500/20">{t('orders.paid')}</Badge>;
  if (status === 'pending') return <Badge className="bg-yellow-500/10 text-yellow-500 border-yellow-500/20">{t('orders.pending')}</Badge>;
  if (status === 'cancelled') return <Badge className="bg-red-500/10 text-red-500 border-red-500/20">{t('orders.cancelled')}</Badge>;
  return <Badge variant="secondary">{status}</Badge>;
}

function PrintStatusBadge({ status, t }) {
  if (status === 'processing') return <Badge className="bg-blue-500/10 text-blue-500 border-blue-500/20">{t('prints.processing')}</Badge>;
  if (status === 'shipped') return <Badge className="bg-purple-500/10 text-purple-500 border-purple-500/20">{t('prints.shipped')}</Badge>;
  if (status === 'delivered') return <Badge className="bg-green-500/10 text-green-500 border-green-500/20">{t('prints.delivered')}</Badge>;
  return <Badge className="bg-yellow-500/10 text-yellow-500 border-yellow-500/20">{t('orders.pending')}</Badge>;
}

function OrderSummaryLine({ order, t }) {
  const photoCount = order.photo_ids ? order.photo_ids.length : 0;
  const printCount = order.print_items ? order.print_items.length : 0;
  const parts = [];
  if (order.is_bundle) {
    parts.push(t('bundles.photoBundle') + ' (' + photoCount + ')');
  } else if (photoCount > 0) {
    parts.push(photoCount + ' ' + t('orders.photo') + (photoCount > 1 ? 's' : ''));
  }
  if (printCount > 0) {
    parts.push(printCount + ' ' + t('prints.physicalPrint') + (printCount > 1 ? 's' : ''));
  }
  return <span>{parts.join(' + ')}</span>;
}

function DigitalPhotoGrid({ photoIds, onDownload, downloading, onDownloadAll, zipping, t }) {
  if (!photoIds || photoIds.length === 0) return null;
  return (
    <div className="mb-4">
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t('prints.digitalItems')}</h4>
        {photoIds.length > 1 && onDownloadAll && (
          <Button size="sm" onClick={onDownloadAll} disabled={zipping}
            className="bg-primary hover:bg-primary/90 h-8"
            data-testid="download-all-btn">
            {zipping ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t('orders.preparingZip')}</>
            ) : (
              <><Download className="w-4 h-4 mr-2" />{t('orders.downloadAll')}</>
            )}
          </Button>
        )}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
        {photoIds.map(function(photoId) {
          return (
            <div key={photoId} className="relative group">
              <div className="aspect-square rounded-sm overflow-hidden bg-muted">
                <img src={API + '/photos/' + photoId + '/preview?size=thumb'} alt="Photo" loading="lazy" decoding="async" className="w-full h-full object-cover" />
              </div>
              <Button size="sm" onClick={function() { onDownload(photoId); }} disabled={downloading === photoId}
                className="absolute bottom-2 right-2 bg-primary hover:bg-primary/90 shadow-lg opacity-0 group-hover:opacity-100 transition-opacity" data-testid={'download-' + photoId}>
                {downloading === photoId ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              </Button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function PrintItemsList({ printItems, t }) {
  if (!printItems || printItems.length === 0) return null;
  return (
    <div>
      <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">{t('prints.printItems')}</h4>
      <div className="space-y-2">
        {printItems.map(function(item, idx) {
          const thumbUrl = API + '/photos/' + item.photo_id + '/preview?size=thumb';
          const fullUrl = API + '/photos/' + item.photo_id + '/preview';
          const itemKey = (item.photo_id || 'unknown') + '-' + (item.format_id || idx);
          return (
            <div key={itemKey} className="flex items-center justify-between p-3 bg-secondary/50 rounded-sm gap-3">
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <a href={fullUrl} target="_blank" rel="noopener noreferrer" className="flex-shrink-0">
                  <img
                    src={thumbUrl}
                    alt={t('common.photoAlt')}
                    loading="lazy"
                    decoding="async"
                    className="w-14 h-14 rounded-sm object-cover border border-border hover:opacity-80 transition"
                    data-testid={`print-thumb-${item.photo_id}`}
                  />
                </a>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <Printer className="w-3.5 h-3.5 text-primary flex-shrink-0" />
                    <span className="text-sm font-medium">{item.format_name}</span>
                    <span className="text-xs text-muted-foreground">x{item.quantity}</span>
                  </div>
                  <p className="text-xs text-muted-foreground font-mono truncate mt-0.5" title={item.photo_id}>
                    {item.photo_id}
                  </p>
                </div>
              </div>
              <span className="font-mono text-sm font-bold text-primary flex-shrink-0">CHF {(item.price || 0).toFixed(2)}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ShippingInfo({ address, t }) {
  if (!address) return null;
  return (
    <div className="mt-3 p-3 bg-secondary/30 rounded-sm">
      <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
        <MapPin className="w-3 h-3" />{t('prints.shippingAddress')}
      </div>
      <p className="text-sm">{address.name}, {address.street}, {address.postal_code} {address.city}, {address.country}</p>
    </div>
  );
}

function formatDate(dateString) {
  return new Date(dateString).toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

export default function Orders() {
  const { getAuthHeaders, user: currentUser } = useAuth();
  const { t } = useLanguage();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);
  const [zippingOrderId, setZippingOrderId] = useState(null);
  const [editOrder, setEditOrder] = useState(null);
  const [editStatus, setEditStatus] = useState('');
  const [editPrintStatus, setEditPrintStatus] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [reconciling, setReconciling] = useState(null);

  const isAdmin = currentUser?.role === 'admin';

  const fetchOrders = async function() {
    try {
      const response = await fetch(API + '/orders', { headers: getAuthHeaders(), credentials: 'include' });
      if (response.ok) setOrders(await response.json());
    } catch (error) {
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleReconcile = async function(orderId) {
    setReconciling(orderId);
    try {
      const response = await fetch(API + '/admin/orders/' + orderId + '/reconcile', {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
      });
      const data = await response.json();
      if (!response.ok) {
        toast.error(data?.detail || t('common.error'));
        return;
      }
      if (data.status === 'reconciled_to_paid') {
        toast.success(t('orders.reconciledPaid'));
        fetchOrders();
      } else if (data.status === 'already_paid') {
        toast.info(t('orders.reconciledAlreadyPaid'));
        fetchOrders();
      } else if (data.status === 'still_unpaid') {
        toast.warning(t('orders.reconciledStillUnpaid'));
      } else {
        toast.error(data?.reason || t('common.error'));
      }
    } catch (e) {
      toast.error(t('common.error'));
    } finally {
      setReconciling(null);
    }
  };

  // Intentional: run once on mount. fetchOrders reads only stable refs (getAuthHeaders).
  useEffect(function() { fetchOrders(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDownload = async function(photoId) {
    setDownloading(photoId);
    try {
      const response = await fetch(API + '/photos/' + photoId + '/download', { headers: getAuthHeaders(), credentials: 'include' });
      if (response.status === 410) {
        toast.error(t('orders.photosExpired') + ' ' + t('orders.photosExpiredDesc') + ' hello@fabioendrich.com', { duration: 8000 });
        return;
      }
      if (!response.ok) throw new Error();
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'photo_' + photoId + '.jpg';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success(t('common.download'));
    } catch (error) {
      toast.error(t('common.error'));
    } finally {
      setDownloading(null);
    }
  };

  const handleDownloadAll = async function(orderId) {
    setZippingOrderId(orderId);
    try {
      const response = await fetch(API + '/orders/' + orderId + '/download-all', { headers: getAuthHeaders(), credentials: 'include' });
      if (response.status === 410) {
        toast.error(t('orders.photosExpired') + ' ' + t('orders.photosExpiredDesc') + ' hello@fabioendrich.com', { duration: 8000 });
        return;
      }
      if (!response.ok) throw new Error();
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'order_' + orderId + '.zip';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success(t('common.success'));
    } catch (error) {
      toast.error(t('common.error'));
    } finally {
      setZippingOrderId(null);
    }
  };

  const openEdit = function(order) {
    setEditOrder(order);
    setEditStatus(order.status || 'pending');
    setEditPrintStatus(order.print_status || 'pending');
  };

  const handleSaveEdit = async function() {
    try {
      const body = { status: editStatus };
      if (editOrder.has_prints) body.print_status = editPrintStatus;
      const response = await fetch(API + '/admin/orders/' + editOrder.order_id, {
        method: 'PUT',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body)
      });
      if (!response.ok) throw new Error();
      toast.success(t('common.success'));
      setEditOrder(null);
      fetchOrders();
    } catch (error) {
      toast.error(t('common.error'));
    }
  };

  const handleDelete = async function(orderId) {
    try {
      const response = await fetch(API + '/admin/orders/' + orderId, {
        method: 'DELETE', headers: getAuthHeaders(), credentials: 'include'
      });
      if (!response.ok) throw new Error();
      toast.success(t('common.success'));
      setDeleteConfirm(null);
      fetchOrders();
    } catch (error) {
      toast.error(t('common.error'));
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-6 md:px-12 py-12" data-testid="orders-page">
      <div className="mb-8">
        <h1 className="text-3xl sm:text-4xl font-black tracking-tight mb-2">{t('orders.title')}</h1>
        <p className="text-muted-foreground">{t('orders.description')}</p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : orders.length === 0 ? (
        <div className="text-center py-16">
          <div className="w-20 h-20 bg-muted rounded-full flex items-center justify-center mx-auto mb-6">
            <Package className="w-10 h-10 text-muted-foreground" />
          </div>
          <h3 className="text-xl font-bold mb-2">{t('orders.noOrders')}</h3>
          <p className="text-muted-foreground">{t('orders.noOrdersDesc')}</p>
        </div>
      ) : (
        <div className="space-y-6">
          {orders.map(function(order) {
            const totalAmount = order.total_amount ? order.total_amount.toFixed(2) : '0.00';
            const hasPrintItems = order.has_prints === true;
            return (
              <Card key={order.order_id} className="bg-card border-border" data-testid={'order-' + order.order_id}>
                <CardContent className="p-6">
                  <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
                    <div>
                      <div className="flex items-center gap-3 mb-1 flex-wrap">
                        <span className="font-mono text-sm text-muted-foreground">{order.order_id}</span>
                        <OrderStatusBadge status={order.status} t={t} />
                        {hasPrintItems && (
                          <div className="flex items-center gap-1">
                            <Printer className="w-3 h-3 text-primary" />
                            <PrintStatusBadge status={order.print_status} t={t} />
                          </div>
                        )}
                        {order.is_bundle && (
                          <Badge className="bg-green-500/10 text-green-500 border-green-500/20">{t('bundles.photoBundle')}</Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Calendar className="w-4 h-4" />
                        {formatDate(order.created_at)}
                      </div>
                      {isAdmin && order.user_email && (
                        <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1">
                          <User className="w-3 h-3" />
                          {order.user_name || order.user_email}
                        </div>
                      )}
                    </div>

                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        <div className="font-mono text-xl font-bold text-primary">CHF {totalAmount}</div>
                        <div className="text-sm text-muted-foreground">
                          <OrderSummaryLine order={order} t={t} />
                        </div>
                      </div>
                      {isAdmin && (
                        <div className="flex flex-col gap-1">
                          <Button variant="ghost" size="icon" onClick={function() { openEdit(order); }}
                            className="h-8 w-8 text-muted-foreground hover:text-primary" data-testid={'edit-order-' + order.order_id}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={function() { setDeleteConfirm(order); }}
                            className="h-8 w-8 text-muted-foreground hover:text-destructive" data-testid={'delete-order-' + order.order_id}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>

                  {order.status === 'paid' && (
                    <div>
                      {isAdmin && order.earliest_deletion_at && order.photos_available !== false && (
                        <div className="mb-4 p-3 rounded-sm border border-amber-500/30 bg-amber-500/10 text-xs flex items-start gap-2" data-testid={`deletion-warning-${order.order_id}`}>
                          <Clock className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                          <div>
                            <span className="font-semibold text-amber-500">{t('orders.adminPhotoDeletion')}:</span>{' '}
                            <span className="font-mono">
                              {new Date(order.earliest_deletion_at).toLocaleDateString(undefined, { day: '2-digit', month: '2-digit', year: 'numeric' })}
                            </span>
                            <span className="text-muted-foreground"> · {t('orders.adminPhotoDeletionHint')}</span>
                          </div>
                        </div>
                      )}
                      {order.photos_available === false && (order.photo_ids && order.photo_ids.length > 0) && (
                        <div className="mb-4 p-4 rounded-sm border border-destructive/30 bg-destructive/10 text-sm">
                          <div className="font-semibold mb-1 text-destructive">{t('orders.photosExpired')}</div>
                          <div className="text-muted-foreground">
                            {t('orders.photosExpiredDesc')}{' '}
                            <a href="mailto:hello@fabioendrich.com" className="text-primary underline hover:no-underline">hello@fabioendrich.com</a>
                          </div>
                        </div>
                      )}
                      {order.photos_available !== false && (
                        <DigitalPhotoGrid
                          photoIds={order.photo_ids}
                          onDownload={handleDownload}
                          downloading={downloading}
                          onDownloadAll={function() { handleDownloadAll(order.order_id); }}
                          zipping={zippingOrderId === order.order_id}
                          t={t}
                        />
                      )}
                      <PrintItemsList printItems={order.print_items} t={t} />
                      <ShippingInfo address={order.shipping_address} t={t} />
                    </div>
                  )}
                  {order.status === 'pending' && (
                    <div className="text-center py-4 text-sm text-muted-foreground space-y-3">
                      <div>{t('orders.downloadPending')}</div>
                      {isAdmin && order.stripe_session_id && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={function() { handleReconcile(order.order_id); }}
                          disabled={reconciling === order.order_id}
                          data-testid={'reconcile-order-' + order.order_id}
                        >
                          {reconciling === order.order_id ? (
                            <><Loader2 className="w-3 h-3 mr-2 animate-spin" />{t('orders.reconciling')}</>
                          ) : (
                            t('orders.reconcileWithStripe')
                          )}
                        </Button>
                      )}
                    </div>
                  )}
                  {order.status === 'cancelled' && (
                    <div className="text-center py-4 text-sm text-muted-foreground">{t('orders.orderCancelled')}</div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Edit Order Dialog */}
      {editOrder && (
        <Dialog open={!!editOrder} onOpenChange={function(open) { if (!open) setEditOrder(null); }}>
          <DialogContent className="bg-card border-border max-w-sm">
            <DialogHeader>
              <DialogTitle>{t('orders.editOrder')}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <p className="text-sm font-mono text-muted-foreground">{editOrder.order_id}</p>

              <div>
                <label className="text-sm font-medium block mb-1">{t('orders.orderStatus')}</label>
                <Select value={editStatus} onValueChange={setEditStatus}>
                  <SelectTrigger className="bg-background border-border" data-testid="edit-order-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    <SelectItem value="pending">{t('orders.pending')}</SelectItem>
                    <SelectItem value="paid">{t('orders.paid')}</SelectItem>
                    <SelectItem value="cancelled">{t('orders.cancelled')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {editOrder.has_prints && (
                <div>
                  <label className="text-sm font-medium block mb-1">{t('orders.printStatus')}</label>
                  <Select value={editPrintStatus} onValueChange={setEditPrintStatus}>
                    <SelectTrigger className="bg-background border-border" data-testid="edit-print-status">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      <SelectItem value="pending">{t('orders.pending')}</SelectItem>
                      <SelectItem value="processing">{t('prints.processing')}</SelectItem>
                      <SelectItem value="shipped">{t('prints.shipped')}</SelectItem>
                      <SelectItem value="delivered">{t('prints.delivered')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <Button onClick={handleSaveEdit} className="flex-1 bg-primary hover:bg-primary/90">{t('common.save')}</Button>
                <Button variant="outline" onClick={function() { setEditOrder(null); }} className="flex-1">{t('common.cancel')}</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Delete Confirmation */}
      {deleteConfirm && (
        <Dialog open={!!deleteConfirm} onOpenChange={function(open) { if (!open) setDeleteConfirm(null); }}>
          <DialogContent className="bg-card border-border max-w-sm">
            <DialogHeader>
              <DialogTitle>{t('orders.confirmDelete')}</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-muted-foreground mb-4">
              {t('orders.confirmDeleteDesc')} <strong>{deleteConfirm.order_id}</strong>?
            </p>
            <div className="flex gap-3">
              <Button onClick={function() { handleDelete(deleteConfirm.order_id); }} variant="destructive" className="flex-1">{t('common.delete')}</Button>
              <Button variant="outline" onClick={function() { setDeleteConfirm(null); }} className="flex-1">{t('common.cancel')}</Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
